import { Filter } from '@/declare/interfaces';
import { defineStore } from 'pinia';
import { useCampaignStore } from '@/store/campaign';
import { useLeadStore } from '@/store/lead';
import { resetAllStores } from '@/store/reset';
import {
  ApiBusinessUserResponse,
  ApiLoginErrorResponse,
  ApiLoginResponse,
  ApiLoginSuccessResponse
} from '@/declare/types/api';
import { refreshFn } from '@/common/lib/refresh';
import router from '@/router/router';
import { useNotificationStore } from '@/store/notification';
import { useUIStore } from '@/store/ui';
import { get, post } from '@/common/lib/api';
import useLocaleStore from '@/store/locale';
import { API_URL } from '@/common/lib/url';
import { showToast } from '@/common/lib/toast';
import axios from '@/common/lib/axios';
import { IUser, UserBusiness } from '@/declare/types/user';
import { USER_ROLES, UserRoleLevels } from '@/declare/enums/user.enum';
import { IBusiness } from '@/declare/types/business';
import { trans } from '@/common/language/translations';

interface AuthState {
  businesses: IBusiness[];
  currentBusiness: IBusiness | null;
  currentUser: IUser | null;
  isLoggedIn: boolean;
  isOwner: boolean;
  imageUpdateKey: number;
}

export const useAuthStore = defineStore({
  id: 'auth-store',
  state: (): AuthState => ({
    businesses: [] as IBusiness[],
    currentBusiness: null,
    currentUser: {} as IUser,
    isLoggedIn: false,
    isOwner: false,
    imageUpdateKey: Date.now()
  }),
  getters: {
    getCurrentUser: state => (state.isLoggedIn ? state.currentUser : null),
    getIsLoggedIn: state => state.isLoggedIn,
    getBusinesses: state => state.businesses,
    getCurrentBusiness: state => state.currentBusiness,
    getIsOwner: state => state.isOwner
  },
  actions: {
    /**
     * Login with password (POST /auth/login)
     */
    async loginWithPassword(
      email: string,
      password: string,
      userLanguage?: string
    ): Promise<{
      success: boolean;
      accessToken?: string;
      user?: IUser;
      error?: string;
    }> {
      const [status, response] = await post<ApiLoginResponse>('auth/login', {
        email,
        password,
        userLanguage
      });

      if (status === 200 && response.success) {
        const successResponse = response as ApiLoginSuccessResponse;
        return {
          success: true,
          accessToken: successResponse.accessToken,
          user: successResponse.user
        };
      } else if (status === 429) {
        return { success: false, error: trans('Too many requests. Try again later.') };
      } else {
        const errorResponse = response as ApiLoginErrorResponse;
        return { success: false, error: errorResponse.message || trans('Login failed') };
      }
    },

    /**
     * Request OTP code (POST /auth/otp/request)
     */
    async requestOtp(
      email: string,
      userLanguage?: string
    ): Promise<{
      success: boolean;
      message?: string;
    }> {
      const [status, response] = await post<{ success: boolean; message: string }>(
        'auth/otp/request',
        {
          email,
          userLanguage
        }
      );

      if (status === 200 && response.success) {
        return {
          success: true,
          message: response.message || trans('If the email exists, a login code has been sent.')
        };
      }

      return {
        success: false,
        message: trans('Failed to send verification code')
      };
    },

    /**
     * Verify OTP code (POST /auth/otp/verify)
     */
    async verifyOtp(
      email: string,
      code: string,
      userLanguage?: string
    ): Promise<{
      success: boolean;
      accessToken?: string;
      user?: IUser;
      error?: string;
    }> {
      const [status, response] = await post<ApiLoginResponse>('auth/otp/verify', {
        email,
        code,
        userLanguage
      });

      if (status === 200 && response.success) {
        const successResponse = response as ApiLoginSuccessResponse;
        return {
          success: true,
          accessToken: successResponse.accessToken,
          user: successResponse.user
        };
      } else if (status === 429) {
        return { success: false, error: trans('Too many attempts. Try again later.') };
      } else if (status === 400) {
        return { success: false, error: trans('Invalid or expired code') };
      } else {
        const errorResponse = response as ApiLoginErrorResponse;
        return { success: false, error: errorResponse.message || trans('Verification failed') };
      }
    },

    /**
     * Register new user (POST /auth/register)
     */
    async register(payload: {
      email: string;
      password: string;
      name?: string;
      phone?: string;
      lang?: string;
    }): Promise<{
      success: boolean;
      accessToken?: string;
      user?: IUser;
      invitationApproved?: boolean;
      error?: string;
      errorCode?: string;
      details?: { rule?: string };
    }> {
      const [status, response] = await post<
        ApiLoginSuccessResponse & { invitationApproved?: boolean }
      >('auth/register', payload);

      if (status === 200 && response.success) {
        return {
          success: true,
          accessToken: response.accessToken,
          user: response.user,
          invitationApproved: response.invitationApproved
        };
      } else if (status === 400) {
        const errorResponse = response as any;
        if (errorResponse.error_code === 'PASSWORD_INVALID') {
          return {
            success: false,
            errorCode: 'PASSWORD_INVALID',
            details: errorResponse.details
          };
        }
      } else if (status === 401 || status === 409) {
        const errorResponse = response as ApiLoginErrorResponse;
        return {
          success: false,
          error: errorResponse.message || trans('Registration failed')
        };
      }

      return {
        success: false,
        error: trans('Registration failed. Please try again.')
      };
    },

    /**
     * Request password reset (POST /auth/password-reset)
     */
    async requestPasswordReset(
      email: string,
      userLanguage?: string
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
    }> {
      const [status, response] = await post<{ success: boolean; message: string }>(
        'auth/password-reset',
        {
          email,
          userLanguage
        }
      );

      if (status === 200 && response.success) {
        return {
          success: true,
          message: response.message
        };
      } else if (status === 404) {
        return {
          success: false,
          error: trans('User not found')
        };
      } else if (status === 429) {
        return {
          success: false,
          error: trans('Too many requests. Try again later.')
        };
      }

      return {
        success: false,
        error: trans('Failed to send reset token')
      };
    },

    /**
     * Reset password with token (POST /auth/reset/:token)
     */
    async resetPassword(
      token: string,
      newPassword: string
    ): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      errorCode?: string;
      details?: { rule?: string };
    }> {
      const [status, response] = await post<{ message: string }>(`auth/reset/${token}`, {
        newPassword
      });

      if (status === 200) {
        return {
          success: true,
          message: response.message || trans('Password reset successfully')
        };
      } else if (status === 400) {
        const errorResponse = response as any;
        if (errorResponse.error_code === 'PASSWORD_INVALID') {
          return {
            success: false,
            errorCode: 'PASSWORD_INVALID',
            details: errorResponse.details
          };
        }
        return {
          success: false,
          error: errorResponse.message || trans('Invalid or expired token')
        };
      }

      return {
        success: false,
        error: trans('Password reset failed')
      };
    },

    /**
     * Legacy login method (kept for compatibility)
     * @deprecated Use loginWithPassword instead
     */
    async login(user: Partial<IUser>): Promise<ApiLoginResponse | null> {
      const result = await this.loginWithPassword(
        user.email!,
        user.password!,
        user.userLanguage ?? undefined
      );

      if (result.success && result.accessToken) {
        localStorage.setItem('token', result.accessToken);
        await this.fetchCurrentUser();
        return {
          success: true,
          accessToken: result.accessToken,
          user: result.user
        } as ApiLoginSuccessResponse;
      } else {
        showToast(result.error || trans('Login failed'), 'error');
        return null;
      }
    },

    async postLoginActions(token: string) {
      localStorage.setItem('token', token);
      await this.fetchCurrentUser();
    },

    async logout(): Promise<void> {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('businessId');
        resetAllStores([
          useCampaignStore(),
          useLeadStore(),
          useNotificationStore(),
          useUIStore(),
          this
        ]);
        await router.push('/auth/login');
      } catch (error) {
        console.error('An error occurred while logging out the user:', error);
        throw new Error('Failed to log out user');
      }
    },

    async fetchCurrentUser() {
      const campaignStore = useCampaignStore();
      const localeStore = useLocaleStore();

      const [status, response] = await get<{
        success: boolean;
        user: IUser;
        businesses: UserBusiness[];
      }>(`${API_URL}getCurrentUser`);

      if (status !== 200 || !response.success) {
        console.error('Failed to fetch current user: Invalid status or unsuccessful response');
        throw new Error('Failed to fetch current user');
      }

      this.setCurrentUser(response.user);

      if (response.user.userLanguage != null) {
        localeStore.setLanguage(response.user.userLanguage);
      }

      const businessData: IBusiness[] = response.businesses.map(
        (business: UserBusiness) =>
          ({
            ...business.business,
            _id: business.businessId,
            role: business.role
          }) as IBusiness
      );

      if (businessData.length > 0) {
        this.setBusinesses(businessData);
        const url = document.location.pathname;
        const regex = /\/b\/([a-zA-Z0-9]+)/;
        const currentBusiness = url.match(regex)?.[1];

        let selectedBusiness = businessData[0];
        if (currentBusiness) {
          const business = businessData.find(business => business._id === currentBusiness);
          if (business) {
            selectedBusiness = business;
          }
        }
        await this.setCurrentBusiness(selectedBusiness);
        await campaignStore.fetchCampaigns();
      } else {
        console.log('No businesses found for the user');
      }

      this.setLoggedInState(true);
      return response;
    },

    async fetchUsersForBusiness(page = 1, limit = 10, filters: Filter[] = [], search = '') {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        filters: encodeURIComponent(JSON.stringify(filters)),
        search
      });

      let response;

      // Use axios.get directly for search-specific requests to avoid the loader
      if (search.trim().length > 0) {
        response = await axios.get(`${API_URL}users?${params.toString()}`);
      } else {
        // Use the private request function for other cases
        const [status, privateResponse] = await get<ApiBusinessUserResponse>(
          `${API_URL}users?${params.toString()}`
        );

        if (status !== 200 || !privateResponse.success) {
          console.error('Failed to fetch users: Invalid status or unsuccessful response');
          showToast('Failed to fetch users. Please try again.', 'error');
          return null;
        }

        response = { data: privateResponse };
      }

      // Process the response data
      const { users, pagination } = response.data.response;
      return { users, pagination };
    },

    checkPermission(minimumRole: USER_ROLES) {
      if (this.currentBusiness) {
        const userRoleLevel = UserRoleLevels[this.currentBusiness.role];
        const minimumRoleLevel = UserRoleLevels[minimumRole];
        return userRoleLevel <= minimumRoleLevel;
      }
      return true;
    },

    reset() {
      this.$reset();
    },

    async setCurrentBusiness(business: IBusiness) {
      const url = router.currentRoute.value.path;
      const regex = /\/b\/[a-zA-Z0-9]+/;
      const newUrl = url.replace(regex, `/b/${business._id}`);

      // Update the route only if the URL needs to change
      if (url !== newUrl) {
        // Avoid awaiting the `router.push` to ensure no unintended side effects
        router.push(newUrl).catch(err => {
          console.error('Router navigation error:', err);
        });
      }

      // Update the current business state in the store
      this.currentBusiness = business;

      // Persist the business ID in local storage
      localStorage.setItem('businessId', business._id);

      // Clear session storage to refresh any business-specific session state
      sessionStorage.clear();

      // Refetch campaigns for the new business
      const campaignStore = useCampaignStore();
      await campaignStore.fetchCampaigns();

      // Trigger any refresh function (ensure it's safely invoked)
      if (typeof refreshFn === 'function') {
        try {
          refreshFn();
        } catch (error) {
          console.error('Error in refresh function:', error);
        }
      }
    },
    setLoggedInState(isLoggedIn: boolean) {
      this.isLoggedIn = isLoggedIn;
    },
    setBusinesses(businesses: IBusiness[]) {
      this.businesses = businesses;
    },
    setCurrentUser(user: IUser | null) {
      this.currentUser = user;
    }
  },
  persist: false
});

export default useAuthStore;
